import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from "react-router-dom";
import { Col, Row, Card, Button } from 'react-bootstrap';
import EquipmentGroupForm from './EquipmentGroupForm';
import { getEquipmentGroupList, deleteEquipmentGroup } from '../../actions/equipment-group';
import { toDisplayDateString } from '../../helpers/moment-ex';
import { isToday, usePermission } from '../../helpers/common';
import { NOT_APPLICABLE } from '../../constants';
import { DataTable } from '../widgets/DataTable';
import { CardContainer } from '../widgets/CardContainer';
import { Heading5, Icon, Span, Modal } from '../widgets';
import { SubNav } from '../layout/submenu';

const initialState = {
	farmId: null,
	isEditMode: false,
	equipmentGroupInfo: null,
	editKey: 0,
	equipmentGroupId: '',
	equipmentGroupName: '',
	deleteModal: false,
	modal: false
}

const EquipmentGroupList = props => {
	const history = useHistory();
	const dispatch = useDispatch();
	const allow = usePermission();

	const farmId = useSelector(state => state.farm.currentFarm.farm_uuid);
	const { range, custom } = useSelector(state => state.analyze.searchParams);
	const isRequesting = useSelector(state => state.equipmentGroup.isRequesting);
	const equipmentGroupList = useSelector(state => state.equipmentGroup.equipmentGroupList);

	const allowCreate = allow("equipment-group", "create") ? isToday(range) : false;
	const allowEdit = allow("equipment-group", "edit") ? isToday(range) : false;
	const allowDelete = allow("equipment-group", "delete") ? isToday(range) : false;

	const [state, setState] = useState(initialState);
	const [searchText, setSearchText] = useState('');
	const [sortedList, setSortList] = useState([]);
	const [sort, setSort] = useState({
		direction: 0,
		field: null,
		fieldType: null
	});

	useEffect(() => {
		if (!farmId) return;

		setState({ ...initialState, farmId });
		dispatch(getEquipmentGroupList({ farmId }));
	}, [farmId, custom]);

	const getSortedList = useCallback(() => {
		const { direction, field, fieldType } = sort;

		let list = [...equipmentGroupList];
		if (direction === 1) list = [...list].sort((a, b) => sortFn("asc", fieldType, field, a, b));
		else if (direction === 2) list = [...list].sort((a, b) => sortFn("desc", fieldType, field, a, b));

		return list;
	}, [equipmentGroupList, sort]);

	useEffect(() => {
		if (!equipmentGroupList || equipmentGroupList.length === 0) return;

		const egList = getSortedList();
		setSortList(egList && egList.length > 0 ? egList : equipmentGroupList);
	}, [equipmentGroupList, sort, getSortedList]);

	const handleSort = (sortField, sortFieldType) => {
		const { direction, field } = sort;

		let sortDirection = 1;
		if (sortField === field) {
			if (direction === 1) sortDirection = 2;
			else if (direction === 2) sortDirection = 0;
			else if (direction === 0) sortDirection = 1;
		}

		setSort({
			field: sortField,
			direction: sortDirection,
			fieldType: sortFieldType
		});
	};

	const renderSortOrder = (sortField) => {
		const { direction, field } = sort;

		if (sortField !== field || direction === 0) return <i className="fa fa-caret-down sort-direction hidden" aria-hidden="true" />;
		if (direction === 0) return <i className="fa fa-caret-down sort-direction hidden" aria-hidden="true" />;
		if (direction === 1) return <i className="fa fa-caret-down sort-direction" aria-hidden="true" />;
		if (direction === 2) return <i className="fa fa-caret-up sort-direction" aria-hidden="true" />;
	};

	const sortFn = (sortType, fieldType, keyName, item1, item2) => {
		const a = sortType === "asc" ? item1 : item2;
		const b = sortType === "desc" ? item1 : item2;

		if (fieldType === "text" && keyName !== 'equipment_group_name') {
			if (a[keyName].name.toLowerCase() < b[keyName].name.toLowerCase()) return -1;
			else if (a[keyName].name.toLowerCase() > b[keyName].name.toLowerCase()) return 1;
			else return 0;
		}
		else if (fieldType === "text" && keyName === 'equipment_group_name') {
			if (a[keyName].toLowerCase() < b[keyName].toLowerCase()) return -1;
			else if (a[keyName].toLowerCase() > b[keyName].toLowerCase()) return 1;
			else return 0;
		}
		else if (fieldType === "date") return new Date(a[keyName]) - new Date(b[keyName]);
		else if (fieldType === "number") return a[keyName].name - b[keyName].name;
		else return 0;
	};

	const filterBySearchText = (clearFilter = false) => {
		if (!equipmentGroupList || equipmentGroupList.length === 0) return;

		if (clearFilter || !searchText || searchText.trim().length === 0) {
			setSortList(equipmentGroupList);
			return;
		}

		const hasText = elementName => elementName
			.replace(/\s+/g, ' ')
			.toLowerCase()
			.includes(searchText.toLowerCase().trim());

		const list = equipmentGroupList.filter(e => {
			const { equipment_group_name, vehicle, operator, phone_device, implement } = e;
			if (hasText(equipment_group_name) || hasText(vehicle.name) || hasText(operator.name) || hasText(phone_device.name) || hasText(implement.name)) {
				return true;
			}

			return false;
		});

		setSortList(list);
	}

	const handleAdd = () => {
		setState({
			...state,
			isEditMode: false,
			equipmentGroupInfo: null,
			modal: true,
			editKey: state.editKey + 1
		});
	}

	const handleEdit = item => {
		setState({
			...state,
			isEditMode: true,
			equipmentGroupInfo: item,
			modal: true,
			editKey: state.editKey + 1
		});
	}

	const handleDeleteConfirm = () => {
		dispatch(deleteEquipmentGroup({
			equipment_group_uuid: state.equipmentGroupId,
			farm_uuid: state.farmId
		}));
	}

	const handleDelete = (id, name) => {
		setState({ ...state, equipmentGroupId: id, equipmentGroupName: name, deleteModal: true });
	}

	return (
		<CardContainer lg={12} md={12} sm={12} xs={12}>
			<Card className="card-list">
				<Card.Header>
					<Card.Title>
						<Heading5>Equipment Group List</Heading5>
						<span style={{ cursor: "pointer" }}>
							<Icon
								title="Manage Equipment Groups"
								className="fa fa-th float-right"
								onClick={() => {
									const currentPathname = history.location.pathname;
									const newPathname = currentPathname.replace('/list', '/kanban');
									history.push(newPathname);
								}} />
						</span>
					</Card.Title>
				</Card.Header>
				<Card.Body>
					<Row className='d-flex justify-content-between mb-4'>
						<Col className="search-container d-flex m-0">
							<SubNav.SearchBox
								placeholder="Search for vehicle, operator, phone, implement"
								value={searchText}
								onChange={e => setSearchText(e.target.value)}
								onClear={() => { setSearchText(""); filterBySearchText(true) }}
							/>
							<Button
								size="sm"
								style={{ height: '33px' }}
								onClick={() => filterBySearchText()}
								disabled={isRequesting}>
								Search
							</Button>
						</Col>

						<Col className="search-container d-flex m-0 col-6 text-end">
							{allowCreate ?
								<Button
									style={{ height: '33px', fontSize: '12px' }}
									size="md"
									variant="success"
									className="ml-auto"
									onClick={handleAdd}>
									<Icon className="fa fa-plus mr-1" />
									Add Equipment Group
								</Button>
								: null}
						</Col>
					</Row>
					<Row>
						<Col lg={12} md={12} sm={12}>
							<DataTable>
								<DataTable.Header>
									<DataTable.Row>
										<DataTable.Cell style={{ width: "40px" }}>#</DataTable.Cell>
										<DataTable.Cell>
											<span
												onClick={() => handleSort("equipment_group_name", "text")}
												style={{ cursor: 'pointer' }}>
												Equipment Group Name {renderSortOrder("equipment_group_name")}
											</span>
										</DataTable.Cell>
										<DataTable.Cell>
											<span
												onClick={() => handleSort("vehicle", "text")}
												style={{ cursor: 'pointer' }}>
												Vehicle {renderSortOrder("vehicle")}
											</span>
										</DataTable.Cell>
										<DataTable.Cell>
											<span
												onClick={() => handleSort("implement", "text")}
												style={{ cursor: 'pointer' }}>
												Implement {renderSortOrder("implement")}
											</span>
										</DataTable.Cell>

										<DataTable.Cell>
											<span
												onClick={() => handleSort("phone_device", "text")}
												style={{ cursor: 'pointer' }}>
												Phone Device {renderSortOrder("phone_device")}
											</span>
										</DataTable.Cell>

										<DataTable.Cell>
											<span
												onClick={() => handleSort("can_device", "text")}
												style={{ cursor: 'pointer' }}>
												CAN Device {renderSortOrder("can_device")}
											</span>
										</DataTable.Cell>
										<DataTable.Cell>
											<span
												onClick={() => handleSort("operator", "text")}
												style={{ cursor: 'pointer' }}>
												Operator {renderSortOrder("operator")}
											</span>
										</DataTable.Cell>
										<DataTable.Cell style={{ width: "15%" }} >
											<span
												onClick={() => handleSort("start_date", "date")}
												style={{ cursor: 'pointer' }}>
												Start Date {renderSortOrder("start_date")}
											</span>

										</DataTable.Cell>
										{(allowEdit || allowDelete) && <DataTable.Cell style={{ width: "64px" }}>Action</DataTable.Cell>}
									</DataTable.Row>
								</DataTable.Header>
								<DataTable.Body>
									{sortedList && sortedList.length > 0 ?
										sortedList.map((e, i) => (
											<DataTable.Row className={!e.alive ? "not-alive-txt" : ""} key={i}>
												<DataTable.Cell style={{ width: "40px" }}>{i + 1}</DataTable.Cell>
												<DataTable.Cell>{e.equipment_group_name}</DataTable.Cell>
												<DataTable.Cell>{e.vehicle.name}</DataTable.Cell>
												<DataTable.Cell>{e.implement.name === '' ? NOT_APPLICABLE : e.implement.name}</DataTable.Cell>
												<DataTable.Cell>{e.phone_device.name === '' ? NOT_APPLICABLE : e.phone_device.name}</DataTable.Cell>
												<DataTable.Cell>{e.can_device.name === '' ? NOT_APPLICABLE : e.can_device.name}</DataTable.Cell>
												<DataTable.Cell>{e.operator.name}</DataTable.Cell>
												<DataTable.Cell style={{ width: "15%" }}>{toDisplayDateString(e.vehicle.start_date)}</DataTable.Cell>
												{(allowEdit || allowDelete) && <DataTable.Cell style={{ width: "64px" }} className="center">
													{allowEdit && <Span onClick={() => handleEdit(e)} className="btn-icon mr-2">
														<Icon className="fa fa-pencil" title="Edit"></Icon>
													</Span>}
													{allowDelete && <Span className="btn-icon">
														<Icon className="fa fa-trash" title="Delete"
															onClick={() => handleDelete(e.equipment_group_uuid, e.equipment_group_name)}></Icon>
													</Span>}
												</DataTable.Cell>}
											</DataTable.Row>)) : null}
								</DataTable.Body>
							</DataTable>
							{isRequesting ? <DataTable.Loading /> :
								equipmentGroupList.length === 0 && <DataTable.RecordsNotFound />}
						</Col>
					</Row>
				</Card.Body>
			</Card>

			<Modal.Confirm
				isOpen={state.deleteModal}
				onConfirm={handleDeleteConfirm}
				iconClassName="fa fa-trash"
				body={`Do you really want to delete ${state.equipmentGroupName}?`}
				onHide={() => { setState({ ...state, deleteModal: !state.deleteModal }); }} />

			{
				state.modal &&
				<EquipmentGroupForm
					match={props.match}
					isOpen={state.modal}
					onHide={() => { setState({ ...state, modal: !state.modal }); }}
					isEditMode={state.isEditMode}
					editKey={state.editKey}
					equipmentGroupInfo={state.equipmentGroupInfo} />
			}
		</CardContainer >
	);
}

export default EquipmentGroupList;